import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { checkForExtension } from 'utils/checkForBugherdExtension';
import WizardShell from './WizardShell';
import YourProject from './steps/YourProject';
import InstallBugHerd from './steps/InstallBugHerd';
import InstallBugHerdDemo from './steps/InstallBugHerdDemo';
import TryBugHerd from './steps/TryBugHerd';
import { DEMO_SITE_URL, WelcomeToBugherdProps, WizardStep } from './types';

const WelcomeToBugherd: React.FC<WelcomeToBugherdProps> = props => {
  const {
    user,
    project,
    apiKey,
    bugherdUrl,
    activeExperiments,
    pusherApiKey,
    pusherChannelAuthEndpoint
  } = props;
  const firstName = user?.name
    ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
    : 'there';

  // Lifted here so Step 1's API save is reflected in Step 3's "Open website"
  // button — the server-rendered project.devurl is stale after Step 1 updates it.
  const [devurl, setDevurl] = useState<string | null>(project.devurl);

  // Derived from devurl (rather than a separate flag) so the demo experience
  // survives a page refresh: choosing the demo persists DEMO_SITE_URL as the
  // project's devurl, so it's still recognised after a server re-render.
  const isDemo = devurl === DEMO_SITE_URL;

  // If the BugHerd extension is already installed, there's nothing to install,
  // so Step 2 is skipped and the user is sent straight to Step 3. The check is
  // kicked off on mount (Step 1) so the result is ready by the time they'd
  // reach Step 2 — no perceptible delay in the normal flow. `null` while the
  // handshake is still resolving. Only `true` triggers the skip.
  const [extensionInstalled, setExtensionInstalled] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    let mounted = true;

    checkForExtension().then(exists => {
      if (mounted) setExtensionInstalled(exists);
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <BrowserRouter basename="/welcome-to-bugherd">
      <Routes>
        <Route
          path="/create"
          element={
            <WizardShell
              currentStep={WizardStep.YOUR_PROJECT}
              firstName={firstName}
              activeExperiments={activeExperiments}
              userEmail={user?.email}
            >
              <YourProject
                project={project}
                onDevurlSaved={setDevurl}
                activeExperiments={activeExperiments}
              />
            </WizardShell>
          }
        />
        <Route
          path="/install"
          element={
            extensionInstalled ? (
              // Extension already installed (Chrome/Firefox/Safari/Edge) —
              // nothing to install, so skip Step 2 for both the real-website
              // and demo paths and land the user on Step 3.
              <Navigate to="/try" replace />
            ) : (
              <WizardShell
                currentStep={WizardStep.INSTALL}
                firstName={firstName}
                activeExperiments={activeExperiments}
                userEmail={user?.email}
              >
                {isDemo ? (
                  <InstallBugHerdDemo />
                ) : (
                  <InstallBugHerd
                    projectId={project.id}
                    apiKey={apiKey}
                    bugherdUrl={bugherdUrl}
                  />
                )}
              </WizardShell>
            )
          }
        />
        <Route
          path="/try"
          element={
            <WizardShell
              currentStep={WizardStep.TRY}
              firstName={firstName}
              activeExperiments={activeExperiments}
              userEmail={user?.email}
            >
              <TryBugHerd
                project={project}
                devurl={devurl}
                isDemo={isDemo}
                pusherApiKey={pusherApiKey}
                pusherChannelAuthEndpoint={pusherChannelAuthEndpoint}
              />
            </WizardShell>
          }
        />
        <Route path="*" element={<Navigate to="/create" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default WelcomeToBugherd;
